import React, { Fragment, ReactNode } from "react";
import { Table, MediaQuery } from "@mantine/core";

export interface ResponsiveDataTableColumn<T> {
  key: string;
  label?: ReactNode;
  render: (item: T, isMobile?: boolean) => ReactNode;
  hideOnMobile?: boolean;
  sortable?: boolean;
}

export interface ResponsiveDataTableProps<T> {
  elements: T[];
  columns: ResponsiveDataTableColumn<T>[];
  actionsRender?: (item: T, isMobile: boolean) => ReactNode;
  actionsLabel?: ReactNode;
  verifiedIconRender?: (item: T) => ReactNode;
  rowProps?: (item: T, isMobile: boolean) => Record<string, any>;
  idKey?: keyof T | ((item: T) => string);
  sortColumn?: string | null;
  sortDirection?: "asc" | "desc";
  onSort?: (columnKey: string) => void;
  pagination?: ReactNode;
}

export function ResponsiveDataTable<T>({
  elements,
  columns,
  actionsRender,
  actionsLabel = "Actions",
  verifiedIconRender,
  rowProps,
  idKey = "id" as keyof T,
  sortColumn,
  sortDirection = "asc",
  onSort,
  pagination,
}: ResponsiveDataTableProps<T>) {
  const getId = (item: T, index: number): string => {
    if (typeof idKey === "function") {
      return idKey(item) || String(index);
    }
    return String((item as any)[idKey]) || String(index);
  };

  const mobileColumns = columns.filter((col) => !col.hideOnMobile);

  const renderHeaderCell = (col: ResponsiveDataTableColumn<T>) => {
    const isSortable = onSort && col.sortable !== false;
    const isSorted = sortColumn === col.key;

    if (!isSortable) {
      return <th key={col.key}>{col.label}</th>;
    }

    return (
      <th
        key={col.key}
        onClick={() => onSort(col.key)}
        style={{
          cursor: "pointer",
          userSelect: "none",
        }}
        title={`Sort by ${typeof col.label === "string" ? col.label : col.key}`}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}>
          <span>{col.label}</span>
          <span
            style={{
              fontSize: "0.75rem",
              lineHeight: 1,
              opacity: isSorted ? 1 : 0.35,
              color: isSorted ? "#228be6" : "inherit",
              fontWeight: isSorted ? 700 : 400,
            }}
          >
            {isSorted ? (sortDirection === "asc" ? "▲" : "▼") : "↕"}
          </span>
        </div>
      </th>
    );
  };

  return (
    <>
      <div
        className="captiva-table-wrapper"
        tabIndex={0}
        role="region"
        aria-label="Data table"
      >
        <Table width="100%" mb="xl" striped highlightOnHover className="captiva-data-table captiva-data-table-responsive">
          <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
            <thead>
              <tr>
                {verifiedIconRender && <th></th>}
                {columns.map((col) => renderHeaderCell(col))}
                {actionsRender && <th>{actionsLabel}</th>}
              </tr>
            </thead>
          </MediaQuery>
          <MediaQuery largerThan="sm" styles={{ display: "none" }}>
            <thead>
              <tr>
                {verifiedIconRender && <th></th>}
                {mobileColumns.map((col) => renderHeaderCell(col))}
                {actionsRender && <th>{actionsLabel}</th>}
              </tr>
            </thead>
          </MediaQuery>
          <tbody>
            {elements.map((item, index) => (
              <Fragment key={getId(item, index)}>
                <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                  <tr {...(rowProps ? rowProps(item, false) : {})}>
                    {verifiedIconRender && <td>{verifiedIconRender(item)}</td>}
                    {columns.map((col) => (
                      <td key={col.key}>{col.render(item, false)}</td>
                    ))}
                    {actionsRender && <td onClick={(e) => e.stopPropagation()}>{actionsRender(item, false)}</td>}
                  </tr>
                </MediaQuery>
                <MediaQuery largerThan="sm" styles={{ display: "none" }}>
                  <tr {...(rowProps ? rowProps(item, true) : {})}>
                    {verifiedIconRender && <td>{verifiedIconRender(item)}</td>}
                    {mobileColumns.map((col) => (
                      <td key={col.key}>{col.render(item, true)}</td>
                    ))}
                    {actionsRender && <td onClick={(e) => e.stopPropagation()}>{actionsRender(item, true)}</td>}
                  </tr>
                </MediaQuery>
              </Fragment>
            ))}
          </tbody>
        </Table>
      </div>
      {pagination}
    </>
  );
}

export default ResponsiveDataTable;
